"use client"

import React from "react"
import { GripVertical, X } from "lucide-react"
import { Blade, PillButton } from "@/components/blades/blade"
import { SchemaFieldList } from "@/components/shared/schema-field-list"
import { useExplorerContext } from "@/components/explorer/explorer-context"
import { withField } from "@/lib/field-selection"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"

const CHIP_DRAG_TYPE = "application/x-ats-explorer-field"

export interface FieldsBladeProps {
  open: boolean
  onClose: () => void
  depth?: number
  trail?: string[]
  onTrailClick?: (index: number) => void
}

/**
 * Column picker for any vendor: the live schema with association expansion, search and a
 * custom-field toggle, plus the chosen columns as removable chips at the top, in column
 * order — drag a chip to move the column. Selection applies live to the record list. Where
 * the vendor cannot project fields the choice only narrows what is displayed, which the
 * blade says, because the records arrive whole either way.
 */
export function FieldsBlade({ open, onClose, depth, trail, onTrailClick }: FieldsBladeProps) {
  const { activeEntity, selectedEntity, fieldSchema, fieldOptions, selectedFields, setSelectedFields, moveSelectedField } = useExplorerContext()
  const { t, n } = useTranslation()
  const defaults = activeEntity?.defaultFields ?? []
  const selectedSet = new Set(selectedFields)

  const toggle = (path: string, checked: boolean) => setSelectedFields(withField(selectedFields, path, checked))

  return (
    <Blade
      open={open}
      onClose={onClose}
      size="md"
      depth={depth}
      trail={trail}
      onTrailClick={onTrailClick}
      eyebrow={activeEntity?.displayName}
      title={t("explorer.fields.title")}
      description={t("explorer.fieldsBlade.description")}
      badges={
        <span className="rounded-pill bg-accent px-2.5 py-0.5 text-xs font-bold text-accent-foreground">
          {t((selectedFields.length === 1 ? "explorer.fieldsBlade.selectedOne" : "explorer.fieldsBlade.selectedOther"), { count: n(selectedFields.length) })}
        </span>
      }
      footer={
        <>
          <PillButton variant="ghost" onClick={() => setSelectedFields(defaults)} disabled={defaults.length === 0}>
            {t("explorer.fieldsBlade.resetDefaults")}
          </PillButton>
          <PillButton onClick={onClose}>{t("common.actions.done")}</PillButton>
        </>
      }
    >
      {!(selectedEntity?.supportsFieldSelection ?? true) && (
        <p className="mb-3 text-xs text-muted-foreground">{t("explorer.fieldsBlade.displayOnlyNote")}</p>
      )}
      {selectedFields.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-1.5" onDragOver={(event) => event.preventDefault()} onDrop={(event) => moveSelectedField(event.dataTransfer.getData(CHIP_DRAG_TYPE), null)}>
          {selectedFields.map((path) => (
            <span
              key={path}
              draggable
              onDragStart={(event) => event.dataTransfer.setData(CHIP_DRAG_TYPE, path)}
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => {
                event.stopPropagation()
                const dragged = event.dataTransfer.getData(CHIP_DRAG_TYPE)
                if (dragged && dragged !== path) moveSelectedField(dragged, path)
              }}
              title={t("explorer.fieldsBlade.dragChip")}
              className="inline-flex cursor-grab items-center gap-1 rounded-pill border border-chip-border bg-chip py-0.5 pl-1.5 pr-1 font-mono text-[11px] text-[var(--th-active-pill-fg)]"
            >
              <GripVertical className="h-3 w-3 text-muted-foreground/60" />
              {path}
              <button type="button" className="rounded-full p-0.5 hover:bg-chip-remove" aria-label={t("explorer.common.removeAria", { name: path })} onClick={() => toggle(path, false)}>
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <SchemaFieldList
        fields={fieldSchema.fields}
        isLoading={fieldSchema.isLoading && fieldSchema.fields === null}
        selected={selectedSet}
        onToggle={toggle}
        expandedPaths={fieldSchema.expandedPaths}
        expandingPath={fieldSchema.expandingPath}
        onExpand={(path) => void fieldSchema.expand(path)}
        onSelectDefaults={() => setSelectedFields(defaults)}
        onSelectAllColumns={() => setSelectedFields(fieldOptions)}
        onClear={() => setSelectedFields([])}
      />
    </Blade>
  )
}
